import { Form, Input, Select, Row, Col, Checkbox, Button } from 'antd';
import { useIntl } from 'react-intl';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { InputDate, InputDecimal, InputText } from '@/components/_common';
import AreaSelectField from '@/components/_common/AreaSelectField';
import { FormInstance } from 'antd/es/form/Form';
import {
  EmployeeProfessionalAcademics,
  EmployeeProfessionalFormValues,
  EmployeeProfessionalTypes,
} from '@/core/domain/enterprise/Employees/Employees.types';
import { DefaultOptionType } from 'antd/lib/select';
import FunctionsSelect from '@/components/_common/FunctionsSelect';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { EmployeeFunction } from '@/core/domain/enterprise/Functions/Functions.types';
import { useEffect, useState } from 'react';
import { useDisclosure } from '@/hooks';
import FunctionsCreateModal from '@/components/Functions/FunctionsCreateModal';
import FunctionsUpdateModal from '@/components/Functions/FunctionsUpdateModal';
import { useFunctionMutation } from '@/core/domain/enterprise/Functions/Functions.hooks';
import { isAppPortocar } from '@/index';

type Props = {
  form: FormInstance<EmployeeProfessionalFormValues>;
};

const typeOptions: DefaultOptionType[] = [
  { value: EmployeeProfessionalTypes.Administrative, label: 'Administrativo' },
  { value: EmployeeProfessionalTypes.Productive, label: 'Produtivo' },
];

const academicOptions: DefaultOptionType[] = [
  {
    value: EmployeeProfessionalAcademics.medioIncompleto,
    label: EmployeeProfessionalAcademics.medioIncompleto,
  },
  {
    value: EmployeeProfessionalAcademics.medioCompleto,
    label: EmployeeProfessionalAcademics.medioCompleto,
  },
  {
    value: EmployeeProfessionalAcademics.superiorIncompleto,
    label: EmployeeProfessionalAcademics.superiorIncompleto,
  },
  {
    value: EmployeeProfessionalAcademics.superiorCompleto,
    label: EmployeeProfessionalAcademics.superiorCompleto,
  },
  {
    value: EmployeeProfessionalAcademics.posInompleto,
    label: EmployeeProfessionalAcademics.posInompleto,
  },
  {
    value: EmployeeProfessionalAcademics.posCompleto,
    label: EmployeeProfessionalAcademics.posCompleto,
  },
  {
    value: EmployeeProfessionalAcademics.mestradoIncompleto,
    label: EmployeeProfessionalAcademics.mestradoIncompleto,
  },
  {
    value: EmployeeProfessionalAcademics.mestradoCompleto,
    label: EmployeeProfessionalAcademics.mestradoCompleto,
  },
  {
    value: EmployeeProfessionalAcademics.completePrimaryEducation,
    label: EmployeeProfessionalAcademics.completePrimaryEducation,
  },
  {
    value: EmployeeProfessionalAcademics.incompletePrimaryEducation,
    label: EmployeeProfessionalAcademics.incompletePrimaryEducation,
  },
];

const ProfessionalForm: React.FC<Props> = ({ form }) => {
  const intl = useIntl().formatMessage;
  const functionId = Form.useWatch('functionId', form);
  const isEstimator = !!Form.useWatch('estimator', form);
  const employeeFunction = useFunctionMutation();

  const [functionSelected, setFunctionSelected] = useState<EmployeeFunction>();
  const [functionModalCreate, setFunctionModalCreate] = useDisclosure();
  const [functionModalUpdate, setFunctionModalUpdate] = useDisclosure();

  const isEditFunction = functionSelected?.matrixId && functionId;

  useEffect(() => {
    (async () => {
      if (!functionSelected && functionId) {
        const data = await employeeFunction.mutateAsync(functionId);
        setFunctionSelected(data);
      }
    })();
  }, [functionId, functionSelected]);

  return (
    <div>
      <Form layout="vertical" form={form} name="employeeProfessional">
        <Form.Item hidden name="employeeId" />
        <Form.Item hidden name="id" />

        <Row gutter={[16, 16]}>
          <Col span={24} md={12} lg={10}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.registry' })}
              name="registry"
            >
              <Input maxLength={20} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={4}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.type' })}
              name="type"
            >
              <Select options={typeOptions} style={{ width: '100%' }}></Select>
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={4}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.areas' })}
              name="area"
            >
              <AreaSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.academyLevel' })}
              name="academicLevel"
            >
              <Select
                options={academicOptions}
                style={{ width: '100%' }}
                allowClear
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={5} xxl={6}>
            <FloatingFormItem
              noStyle
              label={intl({ id: 'employee.professional.form.role' })}
              name="functionId"
              style={{ width: '100%' }}
            >
              <FunctionsSelect
                key={`${functionModalCreate}-${functionModalUpdate}`}
                onCallback={(employeeFunction) =>
                  setFunctionSelected(employeeFunction)
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={4} xxl={3}>
            <Button
              block
              type="primary"
              onClick={
                isEditFunction
                  ? setFunctionModalUpdate.open
                  : setFunctionModalCreate.open
              }
              size="large"
            >
              {`${isEditFunction ? 'Editar' : 'Novo'} Cargo`}
            </Button>
          </Col>

          <Col span={24} md={12} lg={5}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.admissionDate' })}
              name="admissionDate"
            >
              <InputDate />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={5}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.workCard' })}
              name="workCard"
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={5}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.pis' })}
              name="pisNumber"
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={4}>
            <FloatingFormItem
              label={`${intl({
                id: 'employee.professional.form.salary',
              })} ($)`}
              name="salary"
            >
              <InputDecimal
                disabled={
                  !hasPermissions([
                    Authorities.REGISTER_EMPLOYEE_READJUST_SALARY,
                  ])
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={5}>
            <FloatingFormItem
              label={intl({
                id: 'employee.professional.form.pieceCommission',
              })}
              name="partCommission"
            >
              <InputDecimal fractions={2} max={100} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={5}>
            <FloatingFormItem
              label={intl({
                id: 'employee.professional.form.serviceCommission',
              })}
              name="serviceCommission"
            >
              <InputDecimal fractions={2} max={100} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={5}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.busLines' })}
              name="busLines"
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={5}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.ticketValue' })}
              name="ticketPrice"
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          {isAppPortocar && isEstimator && (
            <Col span={24} md={12} lg={5}>
              <FloatingFormItem
                label={intl({ id: 'employee.professional.form.vehicleCode' })}
                name="vehicleCode"
              >
                <Input />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24}>
            <FloatingFormItem
              label={intl({ id: 'employee.professional.form.observation' })}
              name="observation"
            >
              <Input.TextArea rows={6} maxLength={200} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="estimator"
              valuePropName="checked"
              getValueFromEvent={(event) => {
                const checked = event.target.checked;

                if (!checked) {
                  form.setFieldValue('vehicleCode', undefined);
                }

                return checked;
              }}
            >
              <Checkbox>
                {intl({ id: 'employee.professional.form.estimator' })}
              </Checkbox>
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <FunctionsCreateModal
        open={functionModalCreate}
        onCancel={setFunctionModalCreate.close}
        onCallback={(employeeFunction) =>
          form.setFieldValue('functionId', employeeFunction.id)
        }
      />

      <FunctionsUpdateModal
        employeeFunction={functionSelected}
        open={functionModalUpdate}
        onCancel={setFunctionModalUpdate.close}
      />
    </div>
  );
};

export default ProfessionalForm;
